<template>
  <div>
    <v-dialog v-model="show" scrollable persistent max-width="900">
      <v-card v-if="show" outlined :loading="isLoading" :disabled="isLoading">
        <v-card-title
          :class="`justify-between text-white ${isEdit && isCreate ? 'blue' : isEdit && !isCreate ? 'bg-yellow-500' : 'green'}`">
          <div>
            <span v-if="isEdit && isCreate">
              <v-icon left dark>mdi-plus-box-multiple</v-icon>{{ $t('customer.add_data_about_companion_customer') }}
            </span>
            <span v-else-if="isEdit && !isCreate">
              <v-icon left dark>mdi-pencil</v-icon>{{ $t('customer.edit_data_about_companion_customer') }}
            </span>
            <span v-else>{{ $t('customer.data_about_companion_customer') }}</span>
          </div>
          <v-btn elevation="0" color="white" @click="show = false" icon small v-show="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div class="grid grid-cols-1 gap-2 pt-2">
            <CardReaderBox v-if="isCreate || (!isCreate && isEdit)" />
            <h3 class="my-5">{{ $t('customer.general_data') }}</h3>
            <div class="grid grid-cols-5 gap-2">
              <div>
                <span>{{ $t('account.prefix') }}</span>
                <v-combobox
                  flat
                  background-color="grey lighten-4"
                  solo
                  v-model="form.title"
                  :items="prefixName"
                  :label="$t('account.prefix')"
                  :disabled="!isEdit"
                  item-text="text"
                  item-value="text"
                  :return-object="false"
                >
                </v-combobox>
              </div>
              <div class="col-span-2">
                <span>{{ $t('account.first_name') }}</span>
                <span class="text-red-500" style="font-size:20px; vertical-align: sub;" v-if="!form.firstName">*</span>
                <v-text-field flat background-color="grey lighten-4" solo :label="$t('account.first_name')" v-model="form.firstName"
                  :rules="[rules.firstName]" :disabled="!isEdit"></v-text-field>
              </div>
              <div class="col-span-2">
                <span>{{ $t('account.last_name') }}</span>
                <span class="text-red-500" style="font-size:20px; vertical-align: sub;" v-if="!form.lastName">*</span>
                <v-text-field flat background-color="grey lighten-4" solo :label="$t('account.last_name')" v-model="form.lastName"
                  :rules="[rules.lastName]" :disabled="!isEdit"></v-text-field>
              </div>
            </div>
            <div class="grid grid-cols-2 gap-2">
              <div>
                <span>{{ $t('account.nick_name') }}</span>
                <v-text-field flat background-color="grey lighten-4" solo :label="$t('account.nick_name')" v-model="form.nickName"
                  :disabled="!isEdit"></v-text-field>
              </div>
              <div>
                <span>{{ $t('account.date_of_birth_label') }}</span>
                <v-menu ref="birthDateMenu" v-model="birthDate" :close-on-content-click="false"
                  :return-value.sync="form.dateOfBirth" transition="scale-transition" offset-y min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field :value="$helper.datelabel(form.dateOfBirth)" :label="$t('validate.click_here_to_select_date')"
                      prepend-inner-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" flat
                      background-color="grey lighten-4" solo></v-text-field>
                  </template>
                  <v-date-picker v-model="form.dateOfBirth" no-title scrollable
                    :header-date-format="$helper.headerDateFormat" :year-format="$helper.yearFormat" locale="th"
                    :weekday-format="$helper.formatWeekday" :month-format="$helper.formatMonth">
                    <v-spacer></v-spacer>
                    <v-btn text color="error" @click="birthDate = false">{{ $t('cancel') }}</v-btn>
                    <v-btn text color="success" @click="$refs.birthDateMenu.save(form.dateOfBirth)">{{ $t('ok') }}</v-btn>
                  </v-date-picker>
                </v-menu>
              </div>
            </div>
            <div class="grid grid-cols-2 gap-2">
              <div>
                <span>{{ $t('account.gender') }}</span>
                <v-select flat v-model="form.gender" :items="[
                  { title: $t('account.male'), value: 'm' },
                  { title: $t('account.female'), value: 'f' },
                ]" item-text="title" item-value="value" background-color="grey lighten-4" solo :label="$t('account.gender')"
                  :disabled="!isEdit" :no-data-text="$t('noinfo')"></v-select>
              </div>
              <div>
                <span>{{ $t('account.nationality') }}</span>
                <v-autocomplete flat v-model="form.nationality" :items="jsonNationality" item-text="title" item-value="id"
                  background-color="grey lighten-4" solo :label="$t('account.nationality')" :disabled="!isEdit">
                  <template v-slot:item="{ item }">
                    {{ $t(`nationalities.${item.id}`) }}
                  </template>
                  <template v-slot:selection="{ item }">
                    {{ $t(`nationalities.${item.id}`) }}
                  </template>
                </v-autocomplete>
              </div>
            </div>
            <div class="grid grid-cols-2 gap-2">
              <div>
                <span>{{ $t('account.religion') }}</span>
                <v-combobox
                  flat
                  v-model="form.religion"
                  :items="jsonReligion"
                  item-text="text"
                  item-value="value"
                  background-color="grey lighten-4"
                  solo
                  :label="$t('account.religion')"
                  :disabled="!isEdit"
                  :return-object="false"
                >
                  <template v-slot:selection="{ item }">
                    {{ $te(`religion.${item}`) ? $t(`religion.${item}`) : item }}
                  </template>
                </v-combobox>
              </div>
            </div>
            <div class="grid grid-cols-2 gap-2">
              <EditTaxId showLabel :getLabel="$t('account.citizen_id_card_or_passport_label')" :isCreate="isEdit && isCreate"
                v-model='form.citizenId' :froceDisabled="!isEdit" />
              <div>
                <span>{{ $t('account.laser_id_label') }}</span>
                <v-text-field flat background-color="grey lighten-4" solo :label="$t('account.laser_id_label')"
                  v-model="form.citizenCardLaserNo" :disabled="!isEdit"></v-text-field>
              </div>
            </div>
            <div class="grid grid-cols-2 gap-2">
              <div>
                <span>{{ $t('account.issue_date_card') }}</span>
                <v-menu ref="citizenCardIssueDate" v-model="citizenCardIssueMenu" :close-on-content-click="false"
                  :return-value.sync="form.citizenCardIssueDate" transition="scale-transition" offset-y min-width="auto"
                  :disabled="!isEdit">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field :value="$helper.datelabel(form.citizenCardIssueDate)" :label="$t('validate.click_here_to_select_date')"
                      prepend-inner-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" flat
                      background-color="grey lighten-4" solo :disabled="!isEdit"></v-text-field>
                  </template>
                  <v-date-picker v-model="form.citizenCardIssueDate" no-title scrollable
                    :header-date-format="$helper.headerDateFormat" :year-format="$helper.yearFormat" locale="th"
                    :weekday-format="$helper.formatWeekday" :month-format="$helper.formatMonth" :disabled="!isEdit">
                    <v-spacer></v-spacer>
                    <v-btn text color="error" @click="citizenCardIssueMenu = false">{{ $t('cancel') }}</v-btn>
                    <v-btn text color="success" @click="
                      $refs.citizenCardIssueDate.save(
                        form.citizenCardIssueDate
                      )
                      ">{{ $t('ok') }}</v-btn>
                  </v-date-picker>
                </v-menu>
              </div>
              <div>
                <span>{{ $t('account.expire_date_card_another_form') }}</span>
                <v-menu ref="citizenCardExpiryDate" v-model="citizenCardMenu" :close-on-content-click="false"
                  :return-value.sync="form.citizenCardExpiryDate" transition="scale-transition" offset-y min-width="auto"
                  :disabled="!isEdit">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field :value="$helper.datelabel(form.citizenCardExpiryDate)"
                      :label="$t('validate.click_here_to_select_date')" prepend-inner-icon="mdi-calendar" readonly v-bind="attrs"
                      v-on="on" flat :hint="$t('validate.no_need_to_specify_if_it_is_lifetime_expiration_date')" background-color="grey lighten-4" solo
                      append-icon="mdi-close" @click:append="form.citizenCardExpiryDate = null"
                      :disabled="!isEdit"></v-text-field>
                  </template>
                  <v-date-picker v-model="form.citizenCardExpiryDate" no-title scrollable locale="th"
                    :header-date-format="$helper.headerDateFormat" :year-format="$helper.yearFormat"
                    :weekday-format="$helper.formatWeekday" :month-format="$helper.formatMonth" :disabled="!isEdit">
                    <v-spacer></v-spacer>
                    <v-btn text color="error" @click="citizenCardMenu = false">{{ $t('cancel') }}</v-btn>
                    <v-btn text color="success" @click="
                      $refs.citizenCardExpiryDate.save(
                        form.citizenCardExpiryDate
                      )
                      ">{{ $t('ok') }}</v-btn>
                  </v-date-picker>
                </v-menu>
              </div>
            </div>
            <div class="grid grid-cols-2 gap-2">
              <div>
                <span>{{ $t('customer.citizen_card_images') }}</span>
                <v-file-input ref="citizenImage" accept="image/*" :label="$t('customer.citizen_card_images')"
                  @change="uploadFile($event, form.citizenCardImages_tmp)" v-model="citizenImage_tmp" flat
                  background-color="grey lighten-4" solo :disabled="!isEdit"></v-file-input>
              </div>
            </div>
            <div class="image-list-box" v-if="form.citizenCardImages_tmp && form.citizenCardImages_tmp.length">
              <div outlined v-for="(citizen, index) of form.citizenCardImages_tmp" :key="`citizenxx_${index}`"
                class="image-list-box-in">
                <img v-if="citizen.isCardReader" :src="'data:image/png;base64,' + citizen.imageData" />
                <img v-else :src="citizen.url" />
                <v-btn small elevation="0" color="error" icon :disabled="!isEdit"
                  @click="form.citizenCardImages_tmp.splice(`${index}`, 1)">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </div>
            </div>

            <h3 class="my-5">{{ $t('account.contact_information') }}</h3>
            <div class="grid grid-cols-2 gap-2" v-if="show">
              <div v-for="(tel, index) in form.tels_tmp" :key="`tel-${index}`">
                <PhoneNumber
                  ref="inputPhoneNumber"
                  :tel.sync="form.tels_tmp[index]"
                  :interTel.sync="form.interTels_tmp[index]"
                  :disabled="!isEdit"
                  flat 
                  background-color="grey lighten-4" 
                  solo 
                  :phoneLabel="`${$t('account.phone_number')} #${index + 1}`"
                  required
                  type="text"
                  :append-icon="index - 1 < 0 ? `mdi-plus` : `mdi-minus`"
                  @click:append="index - 1 < 0 ? form.tels_tmp.push('') : removeTel(index)"
                />
              </div>
            </div>

            <h3 class="my-5">{{ $t('account.address_information') }}</h3>
            <div class="grid grid-cols-1 gap-2">
              <div>
                <span>{{ $t('project.house_number_or_building_name_village') }}</span>
                <v-text-field flat background-color="grey lighten-4" solo :label="$t('project.address_line_one')"
                  v-model="form.addressStreet" :disabled="!isEdit"></v-text-field>
              </div>
            </div>
            <div class="grid grid-cols-2 gap-2">
              <div>
                <span>{{ $t('address.sub_district_or_district') }}</span>
                <addressinput-subdistrict :disabled="!isEdit" v-model="form.addressSubDistrict" label=""
                  :inputClass="`grey mb-5 lighten-4 px-3 py-3 rounded elevention-0 w-100 outline-none ${$helper.addressLocaleClass}`" />
              </div>
              <div>
                <span>{{ $t('address.amphoe_or_zone') }}</span>
                <addressinput-district :disabled="!isEdit" v-model="form.addressDistrict" label=""
                  :inputClass="`grey mb-5 lighten-4 px-3 py-3 rounded elevention-0 w-100 outline-none ${$helper.addressLocaleClass}`" />
              </div>
            </div>
            <div class="grid grid-cols-2 gap-2">
              <div>
                <span>{{ $t('project.province') }}</span>
                <addressinput-province :disabled="!isEdit" v-model="form.addressProvince" label=""
                  :inputClass="`grey mb-5 lighten-4 px-3 py-3 rounded elevention-0 w-100 outline-none ${$helper.addressLocaleClass}`" />
              </div>
              <div>
                <span>{{ $t('project.postal_code') }}</span>
                <addressinput-zipcode :disabled="!isEdit" v-model="form.addressZipcode" label=""
                  :inputClass="`grey mb-5 lighten-4 px-3 py-3 rounded elevention-0 w-100 outline-none ${$helper.addressLocaleClass}`" type="text" />
              </div>
            </div>

            <h3 class="my-5">
              {{ $t('companion') }}
              <span class="text-sm">[{{ form.customerCompanions ? form.customerCompanions.length : 0 || 0 }}]</span>
              <span class="text-red-500" style="font-size:20px; vertical-align: sub;"
                v-if="!form.customerCompanions || (form.customerCompanions && form.customerCompanions.length == 0)">*</span>
            </h3>
            <v-card v-for="(customerCompanion, index) in form.customerCompanions" :key="`companion-customer-${index}`">
              <v-card-title class="flex justify-space-between align-center">
                <div>{{ $t('market.companion_to_renter', { index: index + 1 }) }}</div>
                <v-btn color="error" @click="delCustomerCompanions(index)" :disabled="!isEdit">
                  <v-icon left>mdi-window-close</v-icon>
                  {{ $t('market.cancel_companion', { index: index + 1 }) }}
                </v-btn>
              </v-card-title>
              <v-card-text>
                <a style="color:unset;"
                  :href="`/project/${projectId}/market/${marketId}/customer/${customerCompanion.customer.objectId}/view?tabs=0`"
                  target="_blank">
                  <customer-card v-if="customerCompanion.customer" :emails="customerCompanion.customer.emails"
                    :firstName="customerCompanion.customer.firstName" :lastName="customerCompanion.customer.lastName"
                    :tels="customerCompanion.customer.tels" :interTels="customerCompanion.customer.interTels"
                    :companyName="customerCompanion.customer.companyName" :type="customerCompanion.customer.type"
                    :img="customerCompanion.customer.profileImages[0] ? customerCompanion.customer.profileImages[0].url : '/user.png'"
                    :url="`/project/${projectId}/market/${marketId}/customer/${customerCompanion.customer.objectId}/view`"></customer-card>
                </a>
                <div class="text-lg mt-10">
                  {{ $t('market.position_or_department_or_relationship') }}
                  <span class="text-red-500" style="font-size:20px; vertical-align: sub;"
                    v-if="!customerCompanion.name">*</span>
                </div>
                <v-combobox
                  v-if="customerCompanion"
                  :disabled="!isEdit"
                  flat
                  background-color="grey lighten-4"
                  solo
                  v-model="customerCompanion.name"
                  :items="customerCompanionsLabel"
                  :label="$t('market.position_or_department_or_relationship')"
                  item-text="text"
                  item-value="value"
                  :return-object="false"
                >
                  <template v-slot:selection="{ item }">
                    {{ $te(`account.${item}`) ? $t(`account.${item}`) : item }}
                  </template>
                </v-combobox>
              </v-card-text>
            </v-card>
            <div class="flex justify-center my-5">
              <v-btn color="info" @click="searchDialog = true" :loading="isLoadCustomer" :disabled="!isEdit">
                <v-icon left>mdi-plus</v-icon>{{ $t('market.add_renter_companion') }}
              </v-btn>
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="px-2 border-t-2">
          <div class="text-sm text-gray-400 align-self-end">
            <span class="text-red-500" style="font-size:20px; vertical-align: sub;">*</span> {{ $t('validate.required_information') }}
          </div>
          <v-spacer></v-spacer>
          <v-btn color="error darken-1" elevation="0" @click="show = false" v-show="close">{{ $t('close') }}</v-btn>
          <v-btn color="error" @click="isEdit = false" v-if="isEdit && !isCreate">{{ $t('account.cancel_edit') }}</v-btn>
          <v-btn color="warning" @click="isEdit = true" v-if="!isEdit && !isCreate">{{ $t('action_type.edit') }}</v-btn>
          <v-btn v-if="isEdit || isCreate" color="success" elevation="0" :loading="isLoading"
            @click.prevent="createCompanion"
            :disabled="isLoading || !form.firstName || !form.lastName || !form.customerCompanions || (form.customerCompanions && form.customerCompanions.length == 0) || !findNameNull()">{{
              $t('save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- loading dialog -->
    <v-dialog v-model="isFormImageLoading" persistent max-width="500px">
      <v-card color="primary" dark>
        <v-card-text class="text-center">
          <v-progress-circular indeterminate color="white" size="64"></v-progress-circular>
          <v-spacer></v-spacer>
          <span class="display-1 white--text">{{ $t('upload.uploading') }}</span>
        </v-card-text>
        <v-snackbar v-model="isError" color="error">{{ $t('upload.error_add_user') }}</v-snackbar>
      </v-card>
    </v-dialog>
    <dialog-with-timeout v-model="isInputError" :timeout="2000" :classColor="`warning white--text`">
      <span class="text-lg">{{ $t('validate.some_information_is_not_correct_please_check_again') }}</span>
    </dialog-with-timeout>
    <select-customer v-model="searchDialog" @select="selectCustomer" :header="$t('customer.select_customer')"></select-customer>
  </div>
</template>

<script>
import AddressComponent from "./AddressComponent";
import { DataStore } from "vue-thailand-address";
import { mapActions, mapGetters } from "vuex";
import { Banking, Customer, FileUpload } from "../classes";
import DialogWithTimeout from "./DialogWithTimeout.vue";
import SelectCustomer from "./SelectCustomer.vue";
import CustomerCard from "./CustomerCard.vue";
import CardReaderBox from "./CardReaderBox.vue";
import EditTaxId from "@/components/EditTaxId.vue";
import PhoneNumber from "@/components/PhoneNumber.vue";
export default {
  components: {
    AddressComponent,
    DialogWithTimeout,
    SelectCustomer,
    CustomerCard,
    CardReaderBox,
    EditTaxId,
    PhoneNumber
  },
  computed: {
    ...mapGetters({
      getCustomer: "customer/getCustomer",
      getReadData: "socket/getReadData"
    }),
    jsonNationality() {
      const nationality = require("../json/nationalities.json");
      return nationality;
    },
    jsonReligion() {
      const religion = [
        { text: this.$t('religion.ศาสนาพุทธ'), value: "ศาสนาพุทธ" },
        { text: this.$t('religion.ศาสนาอิสลาม'), value: "ศาสนาอิสลาม" },
        { text: this.$t('religion.ศาสนาคริสต์'), value: "ศาสนาคริสต์" },
        { text: this.$t('religion.ศาสนาฮินดู'), value: "ศาสนาฮินดู" },
        { text: this.$t('religion.ศาสนาซิกข์'), value: "ศาสนาซิกข์" },
        { text: this.$t('religion.ลัทธิขงจื๊อ'), value: "ลัทธิขงจื๊อ" },
        { text: this.$t('religion.ศาสนาอื่น ๆ'), value: "ศาสนาอื่น ๆ" },
        { text: this.$t('religion.ไม่มีศาสนา'), value: "ไม่มีศาสนา" },
        { text: this.$t('religion.ไม่ทราบศาสนา'), value: "ไม่ทราบศาสนา" }
      ];
      return religion;
    },
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    },
    isCreate() {
      let isCreate = false;
      if (this.hasCompanion) isCreate = false;
      else if (!this.companionId && this.event == "create") isCreate = true;
      return isCreate;
    },
    isWriteData() {
      let isWriteData = false;
      isWriteData = this.isCreate || (!this.isCreate && this.isEdit) ? true : false;
      return isWriteData;
    },
    employee() {
      return this.$store.getters["employee/getByMarketId"](this.marketId);
    }
  },
  data() {
    return {
      store1: new DataStore(),
      marketId: this.$route.params.marketId,
      projectId: this.$route.params.projectId,
      rules: {
        firstName: value => !!value || this.$t('validate.please_input_real_name'),
        lastName: value => !!value || this.$t('validate.please_input_last_name'),
        taxid: v => (v && v.length <= 13) || this.$t('validate.please_input_not_more_than_thirteen_characters')
      },
      telsRules: [],
      prefixName: [],
      customerCompanionsLabel: [],
      isLoading: false,
      isError: false,
      isEdit: true,
      searchDistrict: "",
      resultSearchDistrict: "",
      searchAmphoe: "",
      resultSearchAmphoe: "",
      citizenImage_tmp: null,
      form: { customerCompanions: [] },
      birthDate: false,
      address: [],
      isFormImageLoading: false,
      citizenCardMenu: false,
      citizenCardIssueMenu: false,
      isInputError: false,
      searchDialog: false,
      isLoadCustomer: false,
      hasCompanion: null
    };
  },
  created() {
    this.telsRules = [
      v => (v && v.length <= 10) || this.$t('validate.please_input_not_more_than_ten_characters')
    ];

    this.prefixName = [
      { text: this.$t('account.mr'), value: "นาย" },
      { text: this.$t('account.ms'), value: "นาง" },
      { text: this.$t('account.mrs'), value: "นางสาว" }
    ];

    this.customerCompanionsLabel = [
      { text: this.$t('account.family_member'), value: "คนในครอบครัว" },
      { text: this.$t('account.relative'), value: "ญาติ" },
      { text: this.$t('account.store_manager'), value: "ผู้จัดการร้าน" },
      { text: this.$t('account.daily_employee'), value: "ลูกจ้างรายวัน" },
      { text: this.$t('account.permanent_employee'), value: "ลูกจ้างประจำ" }
    ];
  },
  watch: {
    show(val) {
      if (val) {
        if (this.companionId) {
          this.getCompanionById(this.companionId);
        } else {
          this.resetForm();
          if (this.isCreate && this.getReadData && this.getReadData.success) {
            this.setData(this.getReadData.data);
          }
        }
      }
    },
    getReadData: {
      async handler(data) {
        if (this.isWriteData && data && data.success) {
          let dataAddress = data.data
          if (dataAddress && dataAddress.subDistrict && dataAddress.district && dataAddress.province) {
            let resDataAddress = await this.store1.dataSource.find(d => d.district == dataAddress.subDistrict && d.amphoe == dataAddress.district && d.province == dataAddress.province)
            if (resDataAddress && resDataAddress.zipcode)
              dataAddress.zipcode = String(resDataAddress.zipcode)
          }
          this.setData(data.data);
        }
      },
      deep: true,
      immediate: true
    }
  },
  props: {
    value: Boolean,
    event: String,
    companionId: String,
    close: {
      type: Boolean,
      default: true
    },
    customer: Object,
    editFirst: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    async getCompanionById(id) {
      if (!id) return;
      try {
        this.isLoading = true;
        this.hasCompanion = await this.$store.dispatch(
          "companion/getCompanion",
          {
            id
          }
        );
        if (this.hasCompanion) {
          if (this.editFirst) this.isEdit = true;
          else this.isEdit = false;
          this.form = {
            ...this.hasCompanion,
            citizenCardImages_tmp: this.hasCompanion.citizenCardImages,
            tels_tmp: this.hasCompanion.tels,
            interTels_tmp: this.hasCompanion.interTels || [""],
            dateOfBirth: this.hasCompanion.dateOfBirth
              ? this.$moment(this.hasCompanion.dateOfBirth.iso).format(
                "YYYY-MM-DD"
              )
              : "",
            citizenCardIssueDate: this.hasCompanion.citizenCardIssueDate
              ? this.$moment(this.hasCompanion.citizenCardIssueDate.iso).format(
                "YYYY-MM-DD"
              )
              : "",
            citizenCardExpiryDate: this.hasCompanion.citizenCardExpiryDate
              ? this.$moment(
                this.hasCompanion.citizenCardExpiryDate.iso
              ).format("YYYY-MM-DD")
              : "",
            customerCompanions: this.hasCompanion.customerCompanions.filter(
              re => !re.isRemoved
            )
          };
        }
        if (this.customer && this.customer.objectId) {
          this.selectCustomer(this.customer);
        }
        this.isLoading = false;
      } catch (e) {
        console.log(e);
        this.isLoading = false;
        this.$toast.show({
          content: this.$t('error'),
          color: "error"
        });
      }
    },
    changePrefix() {
      const localStorageItems = localStorage.getItem("prefixName") || [];
      if (!localStorageItems.includes(this.form.title)) {
        localStorageItems.push(this.form.title);
        localStorage.setItem("prefixName", localStorageItems);
      }
    },
    async uploadFile(e, to = []) {
      if (e) {
        try {
          this.isFormImageLoading = true;
          let data;
          if (e.isCardReader) {
            data = await FileUpload.upload(e.imageData, "png", true);
          } else {
            data = await FileUpload.upload(e);
          }
          to.push(data);
          //
          this.isFormImageLoading = false;
        } catch (err) {
          console.log("~ err", err);
          this.isFormImageLoading = false;
        }
      }
    },
    NumbersOnly(evt) {
      evt = evt ? evt : window.event;
      var charCode = evt.which ? evt.which : evt.keyCode;
      if (
        charCode > 31 &&
        (charCode < 48 || charCode > 57) &&
        charCode !== 46
      ) {
        evt.preventDefault();
      } else {
        return true;
      }
    },
    changeDistrict() {
      if (typeof this.resultSearchDistrict == "object") {
        this.addressSubDistrict = this.resultSearchDistrict.district;
        this.addressDistrict = this.resultSearchDistrict.amphoe;
        this.resultSearchAmphoe = this.resultSearchDistrict.amphoe;
        this.addressProvince = this.resultSearchDistrict.province;
        this.addressZipcode = this.resultSearchDistrict.zipcode;
      }
    },
    async createCompanion() {
      try {
        this.isLoading = true;
        let imageTemp = [];
        for (let image of this.form.citizenCardImages_tmp) {
          let t_img;
          if (image.isCardReader) {
            // await  this.uploadFile(image, t_img);
            t_img = await this.$store.dispatch("markets/updateImageBase64", {
              fileBase64: image.imageData
            });
            imageTemp.push(t_img);
          } else {
            imageTemp.push(image);
          }
        }
        this.form.citizenCardImages = [...imageTemp];
        this.form.tels = this.form.tels_tmp ? [...this.form.tels_tmp] : [];
        this.form.interTels = this.form.interTels_tmp ? [...this.form.interTels_tmp] : [];

        let create;

        if (this.isEdit && this.isCreate) {
          create = await this.$store.dispatch(
            "companion/createCompanion",
            this.form
          );
        } else if (this.isEdit && !this.isCreate) {
          create = await this.$store.dispatch("companion/updateCompanion", {
            ...this.form,
            id: this.hasCompanion.objectId
          });
        }
        if (create && create.objectId) {
          await this.getCompanionById(create.objectId);
          this.isEdit = false;
          this.$toast.show({
            content: this.$t('save_success'),
            color: "success"
          });
          this.$emit("onCreated");
        } else {
          this.$toast.show({
            content: "error",
            color: "error"
          });
        }
        this.isLoading = false;
      } catch (err) {
        console.log("~ err", err);
        this.isLoading = false;
        this.isError = true;
      }
    },
    addTel() {
      this.form.tels.push("");
    },
    removeTel(index) {
      this.form.tels_tmp.splice(index, 1);
      this.form.interTels_tmp.splice(index, 1);
    },
    resetForm() {
      this.isEdit = true;
      this.hasCompanion = null;
      this.citizenImage_tmp = null;
      this.form = {
        projectId: this.projectId,
        tels_tmp: [""],
        interTels_tmp: [""],
        customerCompanions: [],
        citizenCardImages_tmp: []
      };
      if (this.customer && this.customer.objectId) {
        this.selectCustomer(this.customer);
      }
    },
    async selectCustomer(value) {
      this.isLoadCustomer = true;
      let hascus =
        this.form.customerCompanions && this.form.customerCompanions.length > 0
          ? this.form.customerCompanions.find(
            cus =>
              cus.customerId == value.objectId ||
              cus.customer.objectId == value.objectId
          )
          : null;
      if (!hascus) {
        let customer = await this.$store.dispatch("customer/getCustomer", {
          id: value.objectId
        });
        if (customer) {
          this.form.customerCompanions.push({
            customer: customer,
            customerId: customer.id,
            name: ""
          });
        }
      }
      this.isLoadCustomer = false;
      this.searchDialog = false;
    },
    filterActive(contracts) {
      if (contracts && contracts.length) {
        return contracts.filter(ct => ct.isActive);
      }
      return [];
    },
    joinContractAreas(areas) {
      if (areas && areas.length) {
        return areas
          .filter(ar => !ar.isRemoved)
          .map(area => area.name)
          .join(", ");
      }
      return "";
    },
    delCustomerCompanions(index) {
      this.form.customerCompanions.splice(index, 1);
    },
    findNameNull() {
      let reN = this.form.customerCompanions
        .filter(re => !re.isRemoved)
        .find(re => re.name == "");
      if (reN) return false;
      else return true;
    },
    setData(data) {
      let {
        fullNameTH,
        momentDateOfBirth,
        gender,
        momentIssueDate,
        momentExpireDate,
        cid,
        firstAddress,
        subDistrict,
        district,
        province,
        imageData,
        zipcode
      } = data;
      this.form.title = fullNameTH.title || "";
      this.form.firstName = fullNameTH.firstName || "";
      this.form.lastName = fullNameTH.lastName || "";
      this.form.dateOfBirth = momentDateOfBirth.format("YYYY-MM-DD") || "";
      this.form.gender = gender == "male" ? "m" : gender == "female" ? "f" : "";
      this.form.citizenCardIssueDate =
        momentIssueDate.format("YYYY-MM-DD") || "";
      this.form.citizenCardExpiryDate =
        momentExpireDate.format("YYYY-MM-DD") || "";
      this.form.citizenId = cid || "";
      this.form.addressStreet = firstAddress || "";
      this.form.addressSubDistrict = subDistrict || "";
      this.form.addressDistrict = district || "";
      this.form.addressProvince = province || "";
      this.form.addressZipcode = zipcode ? (zipcode).toString() : "" || "";

      let image = {
        name: "Picture from Thai ID card",
        url: "",
        isCardReader: true,
        imageData
      };
      this.form.citizenCardImages_tmp = [image];
    }
  }
};
</script>

<style lang="scss">
@import "../../node_modules/@braid/vue-formulate/themes/snow/snow.scss";

.outline-none {
  outline: none !important;
}

.w-100 {
  width: 100%;
}

.th-address-autocomplete {
  padding: 5px 1rem !important;
  background: white;
}

.card-reader-box {
  // position: sticky;
  // top: 0px;
  // z-index: 1;
  background: rgb(250, 250, 250);
  border: 1px solid rgba(202, 202, 202, 0.473);
  border-radius: 7px;
  min-height: 50px;
  margin-top: 15px;
  padding: 10px;

  .card-reader-box-inner {
    display: flex;
    align-items: center;

    i {
      font-size: 15px;
      margin: 0 5px;
    }
  }
}

.image-list-box {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  position: relative;
  margin-bottom: 15px;

  .image-list-box-in {
    position: relative;
    background: rgba(236, 236, 236, 0.466);
    border: 1px solid rgb(212, 212, 212);
    border-radius: 5px;
    padding: 5px;
    text-align: center;
    min-width: 100px;

    img {
      width: auto;
      height: 120px;
      border-radius: 5px;
      object-fit: contain;
      object-position: center;
    }

    button {
      position: absolute;
      right: 5px;
      top: 5px;
      background: rgba(224, 224, 224, 0.555);
    }
  }
}
</style>
